import { z } from 'zod';
// Use type import to avoid circular dependencies but maintain TypeScript type checking
import { CampaignSchema, type CampaignSchema as CampaignSchemaType } from './campaigns';
import { AudienceSchema } from './audiences';
import { SurveySchema } from './surveys';
import { SignupFormSchema } from './signupForms';

export const CoMarketingApprovalStatusSchema = z.enum(['approved', 'pending', 'denied']);

export const CoMarketingTemplateTypeSchema = z.enum(['sponsored', 'installed']);

export const ResponsibleEmailsSchema = z.array(z.string());

export const BudgetByUIDSchema = z.record(z.number());

export const BudgetMethodSchema = z.enum(['disabled', 'split', 'fixed']);

export const CoMarketingBudgetSchema = z.object({
	isPaid: z.boolean(),
	budgetMethod: BudgetMethodSchema,
	splitCostPercentage: z.number(),
	budgetByUID: BudgetByUIDSchema,
	allowContinuedSending: z.boolean(),
	requiresApproval: z.boolean(),
});

export const CoMarketingTemplateBaseSchema = z
	.object({
		id: z.number(),
		userID: z.string(),
		campaignID: z.string(),
		type: CoMarketingTemplateTypeSchema,
		// Target whitelist is a map of account uids to an array of store ids
		targetWhitelist: z.record(z.array(z.number())),
		supportEmail: z.string(),
		description: z.string(),
		haltDate: z.string(),
		responsibleEmails: ResponsibleEmailsSchema,
		created: z.string(),
		updated: z.string(),
		archived: z.boolean(),
		categories: z.array(z.string()),
	})
	.merge(CoMarketingBudgetSchema);

export const TargetStatusInfoSchema = z.object({
	status: z.boolean(),
	updatedBy: z.string(),
	updatedAt: z.string(),
});

export const SponsoredCoMarketingTemplateSchema = CoMarketingTemplateBaseSchema.extend({
	published: z.boolean(),
	isReusable: z.boolean(),
	isGlobalTemplate: z.boolean(),
	industryRestriction: z.string().optional(),
	installationsByTarget: z.record(TargetStatusInfoSchema),
	rejectionsByTarget: z.record(TargetStatusInfoSchema),
	startDate: z.string(),
	endDate: z.string(),
	createdBy: z.string(),
});

export const CoMarketingInstallInfoSchema = z.object({
	installedFromTemplateID: z.number().optional(),
	installedFromUserID: z.string().optional(),
	installedFromCampaignID: z.string().optional(),
});

export const CoMarketingLinkSchema = z
	.object({
		installedTemplateID: z.number().optional(),
	})
	.merge(CoMarketingInstallInfoSchema);

export const CoMarketingCampaignLinkSchema = CoMarketingLinkSchema.merge(CoMarketingBudgetSchema);

export const CoMarketingAssetLinkSchema = CoMarketingLinkSchema.extend({
	templateLinks: z.record(z.boolean()),
});

export const InstalledCoMarketingTemplateSchema = CoMarketingTemplateBaseSchema.extend({
	approvalStatus: CoMarketingApprovalStatusSchema,
	isCampaignActive: z.boolean(),
	lastStatusChange: z.string(),
	installedBy: z.string(),
}).merge(CoMarketingInstallInfoSchema);

export const CoMarketingEditEventDataSchema = z.object({
	campaign: z
		.lazy(() => {
			const { CampaignSchema } = require('./campaigns');
			return CampaignSchema;
		})
		.optional() as unknown as z.ZodOptional<typeof CampaignSchemaType>,
	audiences: z.record(AudienceSchema).optional(),
	surveys: z.record(SurveySchema).optional(),
	signupForms: z.record(SignupFormSchema).optional(),
	budget: CoMarketingBudgetSchema.optional(),
	metaData: z.any().optional(),
});

export const CoMarketingEditRequestSchema = z.object({
	id: z.number(),
	templateID: z.number(),
	triggerUID: z.string(),
	sponsorUID: z.string(),
	installerID: z.string(),
	approvalStatus: CoMarketingApprovalStatusSchema,
	eventData: CoMarketingEditEventDataSchema,
	canceled: z.boolean(),
	canceledBy: z.string(),
	canceledAt: z.string(),
	created: z.string(),
	createdBy: z.string(),
	updated: z.string(),
	updatedBy: z.string(),
});

export const GetMostRecentCoMarketingEditReqFiltersSchema = z.object({
	templateID: z.number().optional().describe('Template ID to filter by'),
	includeCanceled: z.boolean().optional().describe('Include canceled edit requests'),
	triggerUID: z.string().optional().describe('Ensure the creator of the edit request is the trigger UID'),
	sponsorUID: z.string().optional().describe('Make sure it has the same sponsor UID'),
	installerID: z.string().optional().describe('Make sure it has the same installer ID'),
	approvalStatus: CoMarketingApprovalStatusSchema.optional().describe('Make sure it has the same approval status'),
	userID: z.string().optional().describe('Make sure it has the same user ID'),
	antiApprovalStatus: CoMarketingApprovalStatusSchema.optional().describe('Make sure it has the same anti approval status'),
	includeTemplate: z.boolean().optional().describe('Include the template in the response'),
});

export const CoMarketingEditCommentSchema = z.object({
	id: z.number(),
	userID: z.string(),
	templateID: z.number(),
	eventID: z.number(),
	comment: z.string(),
	originalComment: z.string(),
	createdAt: z.string(),
	createdBy: z.string(),
	updatedAt: z.string(),
	updatedBy: z.string(),
});

export const CoMarketingAssetsSchema = z.object({
	sponsoredTemplate: SponsoredCoMarketingTemplateSchema.optional(),
	installedTemplate: InstalledCoMarketingTemplateSchema.optional(),
	campaign: z
		.lazy(() => {
			const { CampaignSchema } = require('./campaigns');
			return CampaignSchema;
		})
		.optional() as unknown as z.ZodOptional<typeof CampaignSchemaType>,
	audiences: z.record(AudienceSchema).optional(),
	surveys: z.record(SurveySchema).optional(),
	signupForms: z.record(SignupFormSchema).optional(),
});

export const InstalledCoMarketingTemplateWithCampaignSchema = InstalledCoMarketingTemplateSchema.extend({
	campaign: z.lazy(() => {
		const { CampaignSchema } = require('./campaigns');
		return CampaignSchema;
	}) as unknown as typeof CampaignSchemaType,
});

export const SponsoredCoMarketingTemplateWithCampaignSchema = SponsoredCoMarketingTemplateSchema.extend({
	campaign: z.lazy(() => {
		const { CampaignSchema } = require('./campaigns');
		return CampaignSchema;
	}) as unknown as typeof CampaignSchemaType,
});

export const CoMarketingInstallRequestSchema = z.object({
	sponsorID: z.string(),
	templateID: z.number(),
	installerID: z.string(),
	activateCampaign: z.boolean().optional(),
	allowContinuedSending: z.boolean().optional(),
});

export const AudienceChangeSchema = z.object({
	isNewAudience: z.boolean(),
	originalAudience: AudienceSchema,
	requestedAudience: AudienceSchema,
});

export const SurveyChangeSchema = z.object({
	isNewSurvey: z.boolean(),
	originalSurvey: SurveySchema,
	requestedSurvey: SurveySchema,
});

export const SignUpFormChangeSchema = z.object({
	isNewSignUpForm: z.boolean(),
	originalSignUpForm: SignupFormSchema,
	requestedSignUpForm: SignupFormSchema,
});

export const CoMarketingInstallResponseSchema = z.object({
	installedTemplate: InstalledCoMarketingTemplateSchema,
	campaign: CampaignSchema.optional(),
	audienceChanges: z.record(AudienceChangeSchema),
	surveyChanges: z.record(SurveyChangeSchema),
	signUpFormChanges: z.record(SignUpFormChangeSchema),
});

export const CoMarketingEventReqSchema = z.object({
	email: z.string().optional(),
	userID: z.string().optional(),
	installerID: z.string(),
	templateID: z.number(),
	comment: z.string().optional(),
	notify: z.boolean(),
});

export const CoMarketingEditRequestReqSchema = CoMarketingEventReqSchema.extend({
	eventData: CoMarketingEditEventDataSchema,
});

export const CoMarketingEditRequestWithInstalledTemplateWithCampaignSchema = CoMarketingEditRequestSchema.extend({
	template: InstalledCoMarketingTemplateWithCampaignSchema.optional(),
});
